import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static classes = ["hidden"]
  static targets = [
    "bookingField",
    "conditionsOfSaleSelect",
    "conditionsOfSaleContainer",
    "endTimeStaggerIntervalField",
    "eventField",
    "eventFieldContainer",
    "extendBiddingForAllLotsField",
    "hideTemplateField",
    "hideTemplateFieldContainer",
    "templateOptionSelect",
    "timedAndHideTemplateField",
    "timedAndHideTemplateFieldContainer",
    "timedField",
    "timedFieldContainer",
    "typeSelect",
    "webcastAndHideTemplateField",
    "webcastAndHideTemplateFieldContainer",
    "webcastField",
    "webcastFieldContainer",
    "webcastOrEventField",
    "webcastOrEventFieldContainer",
    "webcastOrTimedField",
    "webcastOrTimedFieldContainer"
  ]

  connect() {
    this.typeSelectTarget.dispatchEvent(new Event("change"))

    if (this.hasConditionsOfSaleSelectTarget) {
      this.conditionsOfSaleSelectTarget.dispatchEvent(new Event("change"))
    }

    if (this.hasExtendBiddingForAllLotsFieldTarget) {
      this.extendBiddingForAllLotsFieldTarget.dispatchEvent(new Event("change"))
    }

    this.endTimeStaggerIntervalFieldTarget.dispatchEvent(new Event("change"))

    if (
      this.endTimeStaggerIntervalFieldTarget.disabled &&
      this.hasExtendBiddingForAllLotsFieldTarget &&
      this.extendBiddingForAllLotsFieldTarget.disabled
    ) {
      this.endTimeStaggerIntervalFieldTarget.disabled = false
      this.extendBiddingForAllLotsFieldTarget.disabled = false
    }
  }

  agencyChanged() {
    if (this.hasBookingFieldTarget) {
      this.bookingFieldTarget.disabled = true
    }
  }

  extendBiddingForAllLotsChanged() {
    this.endTimeStaggerIntervalFieldTarget.disabled = this.extendBiddingForAllLotsFieldTarget.checked
  }

  endTimeStaggerIntervalChanged({ target }) {
    if (!this.hasExtendBiddingForAllLotsFieldTarget) {
      return
    }

    this.extendBiddingForAllLotsFieldTarget.disabled = target.value !== "0" && target.value !== ""
  }

  conditionsOfSaleChanged({ target }) {
    const toggle = (target.checked && target.value === "lot") || (!target.checked && target.value === "auction")
    this.conditionsOfSaleContainerTarget.classList.toggle(this.hiddenClass, toggle)
  }

  templateChanged({ target }) {
    const url = new URL(window.location)
    url.searchParams.set("template_id", target.value)

    window.location.href = url.toString()
  }

  toggleFields() {
    const fieldGroups = [
      {
        fieldContainerTargets: this.timedFieldContainerTargets,
        fieldTargets: this.timedFieldTargets,
        show: this.#isTimed()
      },
      {
        fieldContainerTargets: this.webcastFieldContainerTargets,
        fieldTargets: this.webcastFieldTargets,
        show: this.#isWebcast()
      },
      {
        fieldContainerTargets: this.eventFieldContainerTargets,
        fieldTargets: this.eventFieldTargets,
        show: this.#isEvent()
      },
      {
        fieldContainerTargets: this.hideTemplateFieldContainerTargets,
        fieldTargets: this.hideTemplateFieldTargets,
        show: this.#isNotTemplate()
      },
      {
        fieldContainerTargets: this.timedAndHideTemplateFieldContainerTargets,
        fieldTargets: this.timedAndHideTemplateFieldTargets,
        show: this.#isTimed() && this.#isNotTemplate()
      },
      {
        fieldContainerTargets: this.webcastAndHideTemplateFieldContainerTargets,
        fieldTargets: this.webcastAndHideTemplateFieldTargets,
        show: this.#isWebcast() && this.#isNotTemplate()
      },
      {
        fieldContainerTargets: this.webcastOrEventFieldContainerTargets,
        fieldTargets: this.webcastOrEventFieldTargets,
        show: this.#isWebcast() || this.#isEvent()
      },
      {
        fieldContainerTargets: this.webcastOrTimedFieldContainerTargets,
        fieldTargets: this.webcastOrTimedFieldTargets,
        show: this.#isWebcast() || this.#isTimed()
      }
    ]

    fieldGroups.forEach(fieldGroup => {
      const action = fieldGroup.show ? "remove" : "add"
      fieldGroup.fieldContainerTargets.forEach(target => {
        target.classList[action](this.hiddenClass)
      })
      fieldGroup.fieldTargets.forEach(target => {
        this.#setDisabled(target, !fieldGroup.show || target.dataset.disabled === "true")
      })
    })
  }

  // The form connects before Trix defines trix-editor, and a property set on the editor before then
  // shadows its disabled setter, which is what disables the hidden input that carries the value.
  #setDisabled(target, disabled) {
    if (target.localName === "trix-editor") {
      customElements.whenDefined("trix-editor").then(() => {
        target.disabled = disabled
      })
    } else {
      target.disabled = disabled
    }
  }

  #isTimed() {
    return this.#isAuctionType("timed")
  }

  #isWebcast() {
    return this.#isAuctionType("webcast")
  }

  #isEvent() {
    return this.#isAuctionType("event")
  }

  #isAuctionType(type) {
    return this.typeSelectTarget.value === type
  }

  #isNotTemplate() {
    return !this.hasTemplateOptionSelectTarget || !this.templateOptionSelectTarget.checked
  }
}
